import React, { useContext, useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation, useNavigate, matchPath } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useSelector } from 'react-redux';
import { Home, ShoppingBag, ShoppingCart, User, Settings, Menu, Search, X, Clock, ArrowRight, ChevronRight, Package, Sun, Moon } from 'lucide-react';
import { getSearchSuggestions } from '../services/searchService';
import { useViewConfig } from '../hooks/useViewConfig';
import { getIconComponent } from '../utils/iconHelper';
import MobileBackButton from './common/MobileBackButton';
import { useTheme } from '../context/ThemeContext';
import '../styles/navbar.css';

// Image Fallback Component
const ImageWithFallback = ({ src, alt, className }) => {
  const [imgSrc, setImgSrc] = useState(src || '/placeholder_product.png');
  
  useEffect(() => {
    setImgSrc(src || '/placeholder_product.png');
  }, [src]);

  return (
    <img
      src={imgSrc}
      alt={alt}
      className={className}
      onError={() => setImgSrc('/placeholder_product.png')}
      loading="lazy"
    />
  );
};

// Helper function to highlight matching character segments in search query
const highlightMatch = (text, query) => {
  if (!query || !query.trim() || !text) return <span>{text}</span>;
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedQuery})`, 'gi');
  const parts = text.split(regex);
  return (
    <span>
      {parts.map((part, index) => 
        regex.test(part) ? (
          <strong key={index} className="search-highlight">{part}</strong>
        ) : (
          part
        )
      )}
    </span>
  );
};

const Navbar = () => {
  const { user } = useContext(AuthContext);
  const cartItems = useSelector((state) => state.cart.cartItems);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  // Search States
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState({ products: [], categories: [], sections: [], popularSearches: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const searchContainerRef = useRef(null);
  const activeAbortController = useRef(null);
  const suggestionCache = useRef({});

  // Fetch HOME_SECTIONS configuration
  const { data: configData } = useViewConfig("HOME_SECTIONS");

  const sectionsList = useMemo(() => {
    if (!configData?.data?.sections) {
      return ["National Articles", "Premium & Imported", "Accessories", "Fragrance & Grooming"];
    }
    return [...configData.data.sections]
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
      .map(s => s.title || s.name)
      .filter(Boolean);
  }, [configData]);

  // Load recent searches on mount
  const loadRecentSearches = () => {
    const list = JSON.parse(localStorage.getItem('recentSearches') || '[]');
    setRecentSearches(list);
  };

  useEffect(() => {
    loadRecentSearches();
    window.addEventListener('storage', loadRecentSearches);
    return () => window.removeEventListener('storage', loadRecentSearches);
  }, []);

  // Save recent search
  const addRecentSearch = (text) => {
    if (!text || !text.trim() || text.trim().length < 2) return;
    const trimmed = text.trim();
    let list = JSON.parse(localStorage.getItem('recentSearches') || '[]');
    list = list.filter(item => item.text.toLowerCase() !== trimmed.toLowerCase());
    list.unshift({ text: trimmed, timestamp: Date.now() });
    list = list.slice(0, 10); // max 10
    localStorage.setItem('recentSearches', JSON.stringify(list));
    loadRecentSearches();
  };

  // Debounced Suggestion API Fetch
  useEffect(() => {
    setError(null);
    const normalizedQuery = searchQuery.trim().toLowerCase();

    if (searchQuery.trim().length < 2) {
      setSuggestions({ products: [], categories: [], sections: [], popularSearches: ["Jeans", "Shirts", "Belt", "Wallet", "T-SHIRT"] });
      setLoading(false);
      if (activeAbortController.current) {
        activeAbortController.current.abort();
      }
      return;
    }

    // In-memory suggestion cache check
    if (suggestionCache.current[normalizedQuery]) {
      setSuggestions(suggestionCache.current[normalizedQuery]);
      setLoading(false);
      if (activeAbortController.current) {
        activeAbortController.current.abort();
      }
      return;
    }

    setLoading(true);
    const timer = setTimeout(async () => {
      // Abort previous in-flight request
      if (activeAbortController.current) {
        activeAbortController.current.abort();
      }
      const controller = new AbortController();
      activeAbortController.current = controller;

      try {
        const data = await getSearchSuggestions(searchQuery, controller.signal);
        suggestionCache.current[normalizedQuery] = data;
        setSuggestions(data);
        setError(null);
      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }
        console.error("Failed to load suggestions:", err);
        setError("Failed to load suggestions. Please try again.");
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchQuery]);

  // Clean up in-flight requests on unmount
  useEffect(() => {
    return () => {
      if (activeAbortController.current) {
        activeAbortController.current.abort();
      }
    };
  }, []);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Format MOQ Wholesale information
  const getMoqString = (product) => {
    const count = product.moq || 1;
    if (product.moqType === 'set') {
      const pcsCount = count * (product.unitsPerSet || 1);
      return `MOQ ${count} Sets (${pcsCount} Pcs)`;
    }
    return `MOQ ${count} Pcs`;
  };

  // Navigation handlers
  const handleProductClick = (productId) => {
    setIsDropdownOpen(false);
    setIsMobileSearchOpen(false);
    navigate(`/product/${productId}`);
  };

  const handleCategoryClick = (cat) => {
    setIsDropdownOpen(false);
    setIsMobileSearchOpen(false);
    const targetRoute = cat.route || `/shop?categories=${cat._id || cat.value}`;
    navigate(targetRoute);
  };

  const handleSectionClick = (sectionName) => {
    setIsDropdownOpen(false);
    setIsMobileSearchOpen(false);
    navigate(`/shop?section=${encodeURIComponent(sectionName)}`);
  };

  const handleSearchSubmit = (queryText) => {
    const term = queryText || searchQuery;
    if (!term || !term.trim()) return;

    addRecentSearch(term);
    setIsDropdownOpen(false);
    setIsMobileSearchOpen(false);
    navigate(`/shop?search=${encodeURIComponent(term.trim())}`);
  };

  const handleSuggestionQueryClick = (text) => {
    setSearchQuery(text);
    handleSearchSubmit(text);
  };

  const handleRemoveRecentSearch = (e, text) => {
    e.preventDefault();
    e.stopPropagation();
    let list = JSON.parse(localStorage.getItem('recentSearches') || '[]');
    list = list.filter(item => item.text !== text);
    localStorage.setItem('recentSearches', JSON.stringify(list));
    loadRecentSearches();
  };

  // Flattened items list for keyboard navigation
  const navigableItems = useMemo(() => {
    const items = [];
    const sections = suggestions.sections || [];
    const categories = suggestions.categories || [];
    const products = suggestions.products || [];

    // If query length < 2, showcase recent + popular + browse sections
    if (searchQuery.trim().length < 2) {
      recentSearches.slice(0, 5).forEach(item => {
        items.push({ type: 'recent', label: item.text, value: item.text });
      });
      const popular = suggestions.popularSearches || ["Jeans", "Shirts", "Belt", "Wallet", "T-SHIRT"];
      popular.forEach(p => {
        items.push({ type: 'popular', label: p, value: p });
      });
      sectionsList.slice(0, 4).forEach(sec => {
        items.push({ type: 'section', label: sec, value: sec });
      });
    } else {
      // Suggestion Order: 1. Sections, 2. Categories, 3. Products, 4. View All Results
      sections.forEach(sec => {
        items.push({ type: 'section', label: sec, value: sec });
      });
      categories.forEach(cat => {
        items.push({ type: 'category', label: cat.name, value: cat._id, route: cat.route || `/shop?categories=${cat._id}`, category: cat });
      });
      products.forEach(prod => {
        items.push({ type: 'product', label: prod.name, value: prod._id, product: prod });
      });
      if (searchQuery.trim().length >= 2) {
        items.push({ type: 'view-all', label: 'View All Results', value: searchQuery });
      }
    }

    return items;
  }, [searchQuery, suggestions, recentSearches, sectionsList]);

  // Reset keyboard highlight on navigableItems changes
  useEffect(() => {
    setFocusedIndex(-1);
  }, [navigableItems]);

  const handleKeyDown = (e) => {
    if (!isDropdownOpen) {
      if (e.key === 'ArrowDown') {
        setIsDropdownOpen(true);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex((prev) => (prev + 1) % navigableItems.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex((prev) => (prev - 1 + navigableItems.length) % navigableItems.length);
    } else if (e.key === 'Escape') {
      setIsDropdownOpen(false);
      setFocusedIndex(-1);
      e.target.blur();
    } else if (e.key === 'Enter') {
      if (focusedIndex >= 0 && focusedIndex < navigableItems.length) {
        e.preventDefault();
        const item = navigableItems[focusedIndex];
        if (item.type === 'recent' || item.type === 'popular') {
          handleSuggestionQueryClick(item.value);
        } else if (item.type === 'section') {
          handleSectionClick(item.value);
        } else if (item.type === 'category') {
          handleCategoryClick(item);
        } else if (item.type === 'product') {
          handleProductClick(item.value);
        } else if (item.type === 'view-all') {
          handleSearchSubmit(item.value);
        }
      } else {
        handleSearchSubmit();
      }
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const isAccountActive = () => {
    return location.pathname === '/account' || location.pathname === '/profile' || location.pathname === '/login';
  };

  const isAdminActive = () => {
    return location.pathname.startsWith('/admin');
  };

  const isAccountDashboard = location.pathname === '/account' || location.pathname === '/profile';

  // Whitelist of routes where the mobile back button should be shown
  const SHOW_PATTERNS = [
    '/product/:id',
    '/cart',
    '/checkout',
    '/checkout/payment',
    '/login',
    '/register',
    '/verify-otp',
    '/forgot-password',
    '/reset-password',
    '/account',
    '/profile',
    '/ordersuccess',
    '/order-success',
    '/about',
    '/terms-and-conditions',
    '/return',
    '/shipping',
    '/contact',
    '/contact-us',
    '/admin',
    '/admin/*',
  ];

  const showBackButton = SHOW_PATTERNS.some(pattern => 
    matchPath({ path: pattern, end: true }, location.pathname)
  );

  return (
    <>
      <nav className="navbar">
        {showBackButton && <MobileBackButton />}
        {isAccountDashboard && (
          <button 
            className="navbar-menu-btn" 
            onClick={() => window.dispatchEvent(new Event('toggleAccountDrawer'))}
            title="Open Account Menu"
          >
            <Menu size={24} />
          </button>
        )}
        <div className={`navbar-brand ${isAccountDashboard ? 'has-menu-btn' : ''} ${showBackButton ? 'has-back-btn' : ''}`}>
          <Link to="/">
            <img src={theme === 'light' ? '/RegardTraderLogo_black.png' : '/RegardTradersLogo.png'} alt="REGARD TRADERS" style={{ height: '150px', width: '150px', marginTop: '-45px', marginBottom: '-45px', objectFit: 'cover' }} />
          </Link>
        </div>

        {/* Global Desktop Search Bar */}
        <div className="navbar-search-container" ref={searchContainerRef}>
          <div className="navbar-search-input-wrapper">
            <Search className="search-icon-nav" size={18} />
            <input
              type="text"
              placeholder="Search products, categories..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setIsDropdownOpen(true);
              }}
              onFocus={() => setIsDropdownOpen(true)}
              onKeyDown={handleKeyDown}
              className="navbar-search-input"
            />
            {searchQuery && (
              <button className="clear-search-btn" onClick={() => setSearchQuery('')}>
                <X size={16} />
              </button>
            )}
          </div>

          {/* Desktop Search Suggestions Dropdown */}
          {isDropdownOpen && (
            <div className="search-dropdown vertical-dropdown">
              {loading && suggestions.products.length === 0 && (
                <div className="dropdown-loading">
                  <div className="spinner"></div>
                  <span>Searching...</span>
                </div>
              )}

              {error && (
                <div className="dropdown-error">
                  <span>{error}</span>
                </div>
              )}

              {!loading && !error && (
                <div className="search-dropdown-scrollable">
                  {searchQuery.trim().length < 2 ? (
                    /* Initial/Empty State: Browse configs, popular searches & recent searches */
                    <>
                      {recentSearches.length > 0 && (
                        <div className="dropdown-section-group">
                          <h4 className="dropdown-col-title">Recent Searches</h4>
                          <ul className="dropdown-list">
                            {recentSearches.slice(0, 5).map((search, idx) => {
                              const absoluteIdx = navigableItems.findIndex(item => item.type === 'recent' && item.value === search.text);
                              const isHighlighted = absoluteIdx === focusedIndex;
                              return (
                                <li 
                                  key={search.timestamp} 
                                  onClick={() => handleSuggestionQueryClick(search.text)}
                                  className={`dropdown-item query-item ${isHighlighted ? 'highlighted' : ''}`}
                                >
                                  <div className="query-content">
                                    <Clock size={14} className="clock-icon" />
                                    <span>{search.text}</span>
                                  </div>
                                  <button 
                                    className="remove-recent-btn" 
                                    onClick={(e) => handleRemoveRecentSearch(e, search.text)}
                                    title="Remove"
                                  >
                                    <X size={12} />
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}

                      {suggestions.popularSearches && suggestions.popularSearches.length > 0 && (
                        <div className="dropdown-section-group">
                          <h4 className="dropdown-col-title">Popular Searches</h4>
                          <div className="popular-chips-container">
                            <div className="popular-chips">
                              {suggestions.popularSearches.map((term) => {
                                const absoluteIdx = navigableItems.findIndex(item => item.type === 'popular' && item.value === term);
                                const isHighlighted = absoluteIdx === focusedIndex;
                                return (
                                  <button
                                    key={term}
                                    onClick={() => handleSuggestionQueryClick(term)}
                                    className={`popular-chip ${isHighlighted ? 'highlighted' : ''}`}
                                  >
                                    {term}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}

                      {sectionsList.length > 0 && (
                        <div className="dropdown-section-group">
                          <h4 className="dropdown-col-title">Browse Sections</h4>
                          <ul className="dropdown-list">
                            {sectionsList.slice(0, 4).map((sec) => {
                              const absoluteIdx = navigableItems.findIndex(item => item.type === 'section' && item.value === sec);
                              const isHighlighted = absoluteIdx === focusedIndex;
                              return (
                                <li 
                                  key={sec} 
                                  onClick={() => handleSectionClick(sec)}
                                  className={`dropdown-item ${isHighlighted ? 'highlighted' : ''}`}
                                >
                                  <span>{sec}</span>
                                  <ChevronRight size={14} className="chevron-icon" />
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </>
                  ) : (
                    /* Suggestions State */
                    <>
                      {/* 1. Matching Sections */}
                      {suggestions.sections && suggestions.sections.length > 0 && (
                        <div className="dropdown-section-group">
                          <h4 className="dropdown-col-title">Sections</h4>
                          <ul className="dropdown-list">
                            {suggestions.sections.map((sec) => {
                              const absoluteIdx = navigableItems.findIndex(item => item.type === 'section' && item.value === sec);
                              const isHighlighted = absoluteIdx === focusedIndex;
                              return (
                                <li 
                                  key={sec} 
                                  onClick={() => handleSectionClick(sec)}
                                  className={`dropdown-item ${isHighlighted ? 'highlighted' : ''}`}
                                >
                                  <span>{highlightMatch(sec, searchQuery)}</span>
                                  <ChevronRight size={14} className="chevron-icon" />
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}

                      {/* 2. Matching Categories */}
                      {suggestions.categories && suggestions.categories.length > 0 && (
                        <div className="dropdown-section-group">
                          <h4 className="dropdown-col-title">Categories</h4>
                          <ul className="dropdown-list">
                            {suggestions.categories.map((cat) => {
                              const absoluteIdx = navigableItems.findIndex(item => item.type === 'category' && item.value === cat._id);
                              const isHighlighted = absoluteIdx === focusedIndex;
                              const IconComponent = getIconComponent(cat.icon);
                              return (
                                <li 
                                  key={cat._id} 
                                  onClick={() => handleCategoryClick(cat)}
                                  className={`dropdown-item category-item ${isHighlighted ? 'highlighted' : ''}`}
                                >
                                  <div className="category-suggestion-info" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexDirection: 'row' }}>
                                    <IconComponent size={14} className="category-icon" style={{ color: '#ff7a00' }} />
                                    <span className="category-suggestion-name">{highlightMatch(cat.name, searchQuery)}</span>
                                    <span className="category-suggestion-section" style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginLeft: '6px' }}>in {cat.section}</span>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}

                      {/* 3. Matching Products */}
                      {suggestions.products && suggestions.products.length > 0 && (
                        <div className="dropdown-section-group">
                          <h4 className="dropdown-col-title">Products</h4>
                          <ul className="dropdown-list product-list">
                            {suggestions.products.map((prod) => {
                              const absoluteIdx = navigableItems.findIndex(item => item.type === 'product' && item.value === prod._id);
                              const isHighlighted = absoluteIdx === focusedIndex;
                              const primaryImage = typeof prod.images?.[0] === 'string' 
                                ? prod.images[0] 
                                : prod.images?.[0]?.url;

                              return (
                                <li 
                                  key={prod._id} 
                                  onClick={() => handleProductClick(prod._id)}
                                  className={`dropdown-product-item ${isHighlighted ? 'highlighted' : ''}`}
                                >
                                  <ImageWithFallback 
                                    src={primaryImage} 
                                    alt={prod.name} 
                                    className="dropdown-product-thumb" 
                                  />
                                  <div className="dropdown-product-info">
                                    <span className="dropdown-product-name" title={prod.name}>
                                      {highlightMatch(prod.name, searchQuery)}
                                    </span>
                                    <div className="dropdown-product-meta">
                                      <span className="dropdown-product-price">₹{prod.price}/pc</span>
                                      <span className="dropdown-product-moq">{getMoqString(prod)}</span>
                                    </div>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}

                      {/* Empty State */}
                      {!loading && 
                        (!suggestions.sections || suggestions.sections.length === 0) &&
                        (!suggestions.categories || suggestions.categories.length === 0) &&
                        (!suggestions.products || suggestions.products.length === 0) && (
                          <div className="dropdown-empty-state">
                            <Package size={24} style={{ color: 'var(--color-text-muted)', marginBottom: '8px' }} />
                            <span>No suggestions found for &ldquo;{searchQuery}&rdquo;</span>
                          </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* 4. View All Results (Bottom bar) */}
              {searchQuery.trim().length >= 2 && (
                <div 
                  onClick={() => handleSearchSubmit()} 
                  className={`dropdown-view-all ${focusedIndex === navigableItems.findIndex(item => item.type === 'view-all') ? 'highlighted' : ''}`}
                >
                  <span>View All Results &rarr;</span>
                </div>
              )}
            </div>
          )}
        </div>

        <ul className="navbar-links">
          <li><Link to="/shop">Shop</Link></li>
          <li><Link to="/cart">Cart ({cartItems.length})</Link></li>
          {user ? (
            <>
              <li><Link to="/account">Hi, {user.name}</Link></li>
              {user.role === 'admin' && <li><Link to="/admin">Admin</Link></li>}
            </>
          ) : (
            <li><Link to="/login">Login</Link></li>
          )}
          <li className="theme-toggle-li">
            <button 
              className="theme-toggle-btn" 
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </li>
        </ul>

        {/* Global Mobile Navbar Search Bar (Modern inline mockup beside logo) */}
        <div className="navbar-search-mobile-bar" onClick={() => setIsMobileSearchOpen(true)}>
          <Search size={16} className="mobile-search-bar-icon" />
          <span className="mobile-search-bar-placeholder">Search products...</span>
        </div>

        {/* Global Mobile Theme Toggle */}
        <button 
          className="theme-toggle-btn mobile-only-toggle" 
          onClick={toggleTheme}
          aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
        >
          {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
        </button>
      </nav>

      {/* Mobile Fullscreen Search Modal */}
      {isMobileSearchOpen && (
        <div className="mobile-search-modal">
          <div className="mobile-search-header">
            <button className="mobile-search-back-btn" onClick={() => setIsMobileSearchOpen(false)}>
              <ArrowRight size={22} style={{ transform: 'rotate(180deg)' }} />
            </button>
            <div className="mobile-search-input-wrapper">
              <input
                type="text"
                placeholder="Search products, categories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
                className="mobile-search-input"
              />
              {searchQuery && (
                <button className="mobile-search-clear-btn" onClick={() => setSearchQuery('')}>
                  <X size={18} />
                </button>
              )}
            </div>
            <button className="mobile-search-submit-btn" onClick={() => handleSearchSubmit()}>
              Search
            </button>
          </div>

          <div className="mobile-search-body">
            {loading && suggestions.products.length === 0 && (
              <div className="mobile-search-loading">
                <div className="spinner"></div>
                <span>Searching catalog...</span>
              </div>
            )}

            {error && (
              <div className="mobile-search-error" style={{ color: '#ef4444', textAlign: 'center', fontSize: '14px', padding: '24px 0' }}>
                <span>{error}</span>
              </div>
            )}

            {!loading && !error && (
              <div className="mobile-search-scrollable">
                {searchQuery.trim().length < 2 ? (
                  <>
                    {/* Mobile Recent Searches */}
                    {recentSearches.length > 0 && (
                      <div className="mobile-search-section">
                        <div className="mobile-search-section-header">
                          <h4>Recent Searches</h4>
                          <button 
                            onClick={() => {
                              localStorage.setItem('recentSearches', '[]');
                              loadRecentSearches();
                            }}
                            className="clear-all-recent-btn"
                          >
                            Clear All
                          </button>
                        </div>
                        <ul className="mobile-recent-list">
                          {recentSearches.map((search) => (
                            <li 
                              key={search.timestamp} 
                              onClick={() => handleSuggestionQueryClick(search.text)}
                              className="mobile-recent-item"
                            >
                              <div className="mobile-recent-left">
                                <Clock size={16} className="clock-icon" />
                                <span>{search.text}</span>
                              </div>
                              <button 
                                className="remove-recent-btn" 
                                onClick={(e) => handleRemoveRecentSearch(e, search.text)}
                              >
                                <X size={16} />
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Mobile Popular Searches */}
                    {suggestions.popularSearches && suggestions.popularSearches.length > 0 && (
                      <div className="mobile-search-section">
                        <h4>Popular Searches</h4>
                        <div className="mobile-popular-chips">
                          {suggestions.popularSearches.map((term) => (
                            <button
                              key={term}
                              onClick={() => handleSuggestionQueryClick(term)}
                              className="mobile-popular-chip"
                            >
                              {term}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Mobile Browse Sections */}
                    {sectionsList.length > 0 && (
                      <div className="mobile-search-section">
                        <h4>Browse Sections</h4>
                        <ul className="mobile-suggest-list">
                          {sectionsList.map((sec) => (
                            <li key={sec} onClick={() => handleSectionClick(sec)} className="mobile-suggest-item">
                              <span>{sec}</span>
                              <ChevronRight size={18} />
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* 1. Mobile Matching Sections */}
                    {suggestions.sections && suggestions.sections.length > 0 && (
                      <div className="mobile-search-section" style={{ marginBottom: '20px' }}>
                        <h4>Matching Sections</h4>
                        <div className="mobile-popular-chips">
                          {suggestions.sections.map((sec) => (
                            <button
                              key={sec}
                              onClick={() => handleSectionClick(sec)}
                              className="mobile-popular-chip matched-section-chip"
                            >
                              {highlightMatch(sec, searchQuery)}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 2. Mobile Matching Categories */}
                    {suggestions.categories && suggestions.categories.length > 0 && (
                      <div className="mobile-search-section" style={{ marginBottom: '20px' }}>
                        <h4>Categories</h4>
                        <ul className="mobile-suggest-list">
                          {suggestions.categories.map((cat) => {
                            const IconComponent = getIconComponent(cat.icon);
                            return (
                              <li key={cat._id} onClick={() => handleCategoryClick(cat)} className="mobile-suggest-item">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexDirection: 'row' }}>
                                  <IconComponent size={16} className="category-icon" style={{ color: '#ff7a00' }} />
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    <span className="cat-name">{highlightMatch(cat.name, searchQuery)}</span>
                                    <span className="cat-sec">in {cat.section}</span>
                                  </div>
                                </div>
                                <ChevronRight size={18} />
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    {/* 3. Mobile Matching Products */}
                    {suggestions.products && (
                      <div className="mobile-search-section" style={{ marginBottom: '20px' }}>
                        <h4>Products</h4>
                        {suggestions.products.length === 0 ? (
                          <p className="mobile-search-empty-text">No products match your search</p>
                        ) : (
                          <ul className="mobile-product-list">
                            {suggestions.products.map((prod) => {
                              const primaryImage = typeof prod.images?.[0] === 'string' 
                                ? prod.images[0] 
                                : prod.images?.[0]?.url;

                              return (
                                <li 
                                  key={prod._id} 
                                  onClick={() => handleProductClick(prod._id)}
                                  className="mobile-product-item"
                                >
                                  <ImageWithFallback 
                                    src={primaryImage} 
                                    alt={prod.name} 
                                    className="mobile-product-thumb" 
                                  />
                                  <div className="mobile-product-info">
                                    <span className="mobile-product-name">{highlightMatch(prod.name, searchQuery)}</span>
                                    <span className="mobile-product-price">₹{prod.price} / pc</span>
                                    <span className="mobile-product-moq">{getMoqString(prod)}</span>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    )}

                    {/* Mobile View All Results */}
                    {searchQuery.trim().length >= 2 && (
                      <div 
                        onClick={() => handleSearchSubmit()} 
                        className="mobile-view-all"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '16px',
                          background: 'rgba(255, 122, 0, 0.08)',
                          border: '1px solid rgba(255, 122, 0, 0.2)',
                          borderRadius: '8px',
                          color: '#ff7a00',
                          fontWeight: '600',
                          fontSize: '15px',
                          cursor: 'pointer',
                          marginTop: '20px',
                          marginBottom: '20px',
                          textAlign: 'center'
                        }}
                      >
                        <span>View All Results &rarr;</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bottom Navigation Bar (Mobile View Only - Search icon removed) */}
      <nav className="bottom-nav">
        <Link to="/" className={`bottom-nav-item ${isActive('/') ? 'active' : ''}`}>
          <Home size={22} />
          <span>Home</span>
        </Link>
        
        <Link to="/shop" className={`bottom-nav-item ${isActive('/shop') ? 'active' : ''}`}>
          <ShoppingBag size={22} />
          <span>Shop</span>
        </Link>
        
        <Link to="/cart" className={`bottom-nav-item ${isActive('/cart') ? 'active' : ''}`}>
          <div className="bottom-nav-icon-wrapper">
            <ShoppingCart size={22} />
            {cartItems.length > 0 && (
              <span className="bottom-nav-badge">{cartItems.length}</span>
            )}
          </div>
          <span>Cart</span>
        </Link>

        {user && user.role === 'admin' && (
          <Link to="/admin" className={`bottom-nav-item ${isAdminActive() ? 'active' : ''}`}>
            <Settings size={22} />
            <span>Admin</span>
          </Link>
        )}

        <Link to={user ? "/account" : "/login"} className={`bottom-nav-item ${isAccountActive() ? 'active' : ''}`}>
          <User size={22} />
          <span>{user ? 'Account' : 'Login'}</span>
        </Link>
      </nav>
    </>
  );
};

export default Navbar;
